// src/hooks/useDispatcherAnnouncements.js
//
// Watches the things a dispatcher would want to be told about, and says them.
//
// The hard part of this is not detecting the events. It is not announcing
// them. A dispatcher who is told forty things an hour stops hearing any of
// them, and the first thing they do is turn the voice off — at which point the
// one announcement that mattered is lost too. So every rule here is about
// staying quiet:
//
//   • Nothing is announced for a state that already existed when the page
//     opened. Six drivers idle overnight is not six pieces of news at 7am; it
//     is yesterday, and the dispatcher already knows.
//   • Each event is announced once per driver. A driver who stays empty for
//     six hours is mentioned when they go empty, not every refresh.
//   • A quiet period after each announcement, so a burst of Firestore writes
//     cannot become a burst of speech.
//
// What is NOT here, deliberately: traffic delays and LiveLoad matches. Both
// need work beyond watching existing data — see the notes at the bottom.

import { useEffect, useRef } from 'react';
import { speak } from '../utils/announcer';

// A driver has to be idle this long before it is worth saying out loud.
const IDLE_ANNOUNCE_AFTER_MS = 60 * 60 * 1000;   // 1 hour

// How long after page load before anything may be announced. Everything
// arriving in the first seconds is the current state of the world, not news.
const SETTLE_MS = 8000;

const asMillis = (value) => {
  if (!value) return 0;
  if (typeof value.toDate === 'function') return value.toDate().getTime();
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const BUSY_STATUSES = ['dispatched', 'in transit'];

/** No dispatched and no in-transit work. Matches the map's red pins. */
const isEmpty = (driverId, loads) =>
  !loads.some(l => l.driverId === driverId &&
    BUSY_STATUSES.includes(String(l.status || '').toLowerCase()));

/** "two hours" / "an hour and a half" — spoken, not written. */
function spokenDuration(ms) {
  const mins = Math.round(ms / 60000);
  if (mins < 90) return 'about an hour';
  const hours = Math.round(mins / 60);
  if (hours < 24) return `about ${hours} hours`;
  const days = Math.round(hours / 24);
  return days === 1 ? 'over a day' : `about ${days} days`;
}

/** First name only: the voice says it aloud, and surnames add nothing here. */
const firstName = (full) => String(full || 'A driver').trim().split(/\s+/)[0];

/**
 * @param {object[]} drivers   tenant drivers, with lastLocation
 * @param {object[]} loads     current loads
 * @param {object}   options
 * @param {Map<string,string>} options.placeByDriverId  driverId -> "Dallas, TX",
 *   resolved by whoever already has a geocoder open. Absent is fine — the
 *   announcement simply omits the place rather than paying for a lookup here.
 * @param {boolean}  options.enabled
 */
export function useDispatcherAnnouncements(drivers, loads, { placeByDriverId, enabled } = {}) {
  // Everything already said, so nothing repeats. Keyed by event + driver.
  const announced = useRef(new Set());
  const readyAt = useRef(Date.now() + SETTLE_MS);
  // The route-shared timestamp each driver had when we started watching, so an
  // existing shared route is not re-announced on every page load.
  const knownRouteShare = useRef(new Map());
  const seeded = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    if (!Array.isArray(drivers) || !Array.isArray(loads)) return;

    // First pass records the world as it is and says nothing about it.
    if (!seeded.current) {
      loads.forEach(l => {
        const at = asMillis(l.driverRoutePlannedAt);
        if (l.driverId && at) {
          const prev = knownRouteShare.current.get(l.driverId) || 0;
          if (at > prev) knownRouteShare.current.set(l.driverId, at);
        }
      });
      seeded.current = true;
      return;
    }

    if (Date.now() < readyAt.current) return;

    const say = (key, line) => {
      if (announced.current.has(key)) return;
      announced.current.add(key);
      speak(line);
    };

    for (const driver of drivers) {
      const name = firstName(driver.name ||
        [driver.firstName, driver.lastName].filter(Boolean).join(' '));

      // ── 3. Driver shared their route ─────────────────────────────────
      // Checked first because it is the most actionable: the driver has just
      // told dispatch what they intend to do.
      const shares = loads
        .filter(l => l.driverId === driver.id && l.driverRoutePlannedAt)
        .map(l => asMillis(l.driverRoutePlannedAt));
      const latestShare = shares.length ? Math.max(...shares) : 0;
      const previousShare = knownRouteShare.current.get(driver.id) || 0;

      if (latestShare > previousShare) {
        knownRouteShare.current.set(driver.id, latestShare);
        // Keyed by the timestamp, so a driver who re-shares a changed route is
        // announced again — that is new information, not a repeat.
        say(`route:${driver.id}:${latestShare}`,
          `${name} has shared their planned route with you.`);
        continue;
      }

      const empty = isEmpty(driver.id, loads);

      // Going back to work clears the idle announcements, so the next time
      // this driver empties out it is news again.
      if (!empty) {
        announced.current.delete(`empty:${driver.id}`);
        announced.current.delete(`idle:${driver.id}`);
        continue;
      }

      const place = placeByDriverId?.get?.(driver.id);

      // ── 1. Driver is empty, and where ────────────────────────────────
      say(`empty:${driver.id}`,
        place ? `${name} is empty in ${place}.` : `${name} is empty.`);

      // ── 2. Empty for more than an hour ───────────────────────────────
      // A separate, later announcement rather than a louder first one: going
      // empty is normal, staying empty is the thing that costs money.
      const since = asMillis(driver.lastDeliveredAt) || asMillis(driver.lastLocationAt);
      if (since && Date.now() - since >= IDLE_ANNOUNCE_AFTER_MS) {
        say(`idle:${driver.id}`,
          `${name} has been without a load for ${spokenDuration(Date.now() - since)}` +
          (place ? `, in ${place}.` : '.'));
      }
    }
  }, [drivers, loads, placeByDriverId, enabled]);
}

// ── Still to build ───────────────────────────────────────────────────────
//
// 4. Traffic delays. Needs the same route queried twice — TRAFFIC_AWARE and
//    TRAFFIC_UNAWARE — and the difference compared against an hour. The Routes
//    API gives both, and computeRouteLegs already holds the key and the leg
//    cache, so this belongs there as a `delaySeconds` field rather than as a
//    second set of calls from the browser. It also needs a schedule: a delay
//    that appears while nobody has the map open still matters.
//
// 5. LiveLoad matches. Needs a definition of "matches this driver" that is
//    worth interrupting someone for — pickup within some radius of where the
//    driver is or will be, vehicle count inside their remaining capacity, and
//    a price floor. Guessing at those thresholds would produce either silence
//    or constant chatter, so they want deciding with real numbers rather than
//    invented ones.
